import React, { Children, Component } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons'
import { AutoComplete as AntdAutocomplete } from 'antd'
import type { AutoCompleteProps as Props } from 'antd/lib/auto-complete'
import { Input } from '../AppInput'
import './styles.scss'

// props allow you to overwrite any antd AutoComplete properties.
// Please refer to their doc: https://ant.design/components/auto-complete/
export interface AutoCompleteProps extends Props {
  // Props taken from antd Select component.
  // Those work on the autoComplete component but there are not included in
  // antd  AutoCompleteProps...
  labelInValue?: boolean
}

function CaretDownIcon() {
  return <FontAwesomeIcon icon={faCaretDown} className="u-color--neutral-600" />
}
function CaretUpIcon() {
  return <FontAwesomeIcon icon={faCaretUp} className="u-color--neutral-600" />
}

/**
 * This class is a wrap around [antd AutoComplete](https://ant.design/components/auto-complete/) component extending some of
 * the properties to follow our styling guidelines.
 *
 * Please refer to antd documentation for more details.
 *
 * The autocomplete is a normal text input enhanced by a panel of suggested options.
 * The widget is useful for setting the value of a single-line textbox in one of two types of scenarios:
 *    1. The value for the textbox must be chosen from a predefined set of allowed values
 *    2. The textbox may contain any arbitrary value, but it is advantageous to suggest possible values to the user
 *       e.g., a search field may suggest similar or previous searches to save the user time.
 *
 * @example This component can be used with a custom Input if we need a different styled:
 *          \<AppAutoComplete \{..props\}\>\<AppInput suffix=\{\<FontAwesomeIcon icon=\{['fas', 'map-marker-alt']\} /\>\} /\></AppAutoComplete>
 *
 * Enhancements? support prefix icon and Lead Body vs Body text styles from our styleguide
 */
export class AutoComplete extends Component<AutoCompleteProps, { opened: boolean }> {
  // eslint-disable-next-line react/state-in-constructor -- Legacy
  state = { opened: false }

  handleToggleOpened = (open: boolean) => {
    this.setState((prevState) => ({ ...prevState, opened: open }))
  }

  render(): JSX.Element {
    const { children, open, ...autocompleteProps } = this.props
    const { opened } = this.state
    const isOpen = typeof open === 'boolean' ? open : opened
    return (
      <AntdAutocomplete
        style={{ width: '100%' }}
        open={isOpen}
        onDropdownVisibleChange={this.handleToggleOpened}
        {...autocompleteProps}
      >
        {Children.count(children) > 0 ? (
          children
        ) : (
          <Input
            className="app-autocomplete-input"
            suffix={
              <span className="u-flex-align-center">
                <div className="app-autocomplete-input-caret" />
                {isOpen ? <CaretUpIcon /> : <CaretDownIcon />}
              </span>
            }
          />
        )}
      </AntdAutocomplete>
    )
  }
}

export default AutoComplete
